import React, { useEffect, useState } from 'react';
import SailInvoicesPresentational from './SailInvoicesPresentational';
import { useTranslation } from 'react-i18next';
import { useLoading } from '../../commons/Loading';
import { useAuth } from '../../commons/Auth';
import { useAlert } from 'react-alert';
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { list } from '../../lib/fetch/profiles';
import {
  create,
  createCondo,
  list as sailInvoicesList,
  saveCached,
} from '../../lib/fetch/sailInvoices';
import axios from 'axios';

const { REACT_APP_DEFAULT_PAGINATION_LIMIT = 50 } = process.env;

function SailInvoices(props) {
  const { showLoading, hideLoading } = useLoading();
  const [data, setData] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const { t } = useTranslation('SAILINVOICE');
  const [total, setTotal] = useState(0);
  const [{ newToken } = {}] = useAuth();
  const alert = useAlert();

  const [query, setQuery] = useQueryParams({
    year: withDefault(NumberParam, new Date().getFullYear()),
    offset: withDefault(NumberParam, 0),
    customer: withDefault(StringParam, ''),
    search: withDefault(StringParam, ''),
    paid: withDefault(StringParam, ''),
    limit: withDefault(NumberParam, +REACT_APP_DEFAULT_PAGINATION_LIMIT),
  });

  const { year, offset, limit, customer, search, paid } = query;

  const fetchSailInvoices = async () => {
    showLoading();
    const { error, data, headers } = await sailInvoicesList({
      newToken,
      year,
      search,
      offset,
      limit,
      customer,
      paid,
    });
    if (error) {
      setData([]);
      alert.error(t('COMMON:GENERIC_ERROR'));
      hideLoading();
      return;
    }

    setData([...data]);
    setTotal(Number(headers['x-total-count']));
    hideLoading();
  };

  useEffect(() => {
    const fetchProfiles = async () => {
      showLoading();
      const { error, data } = await list({
        newToken,
        limit: 200,
      });
      if (error) {
        setSubjects([]);
        return;
      }
      setSubjects(data);
    };

    fetchProfiles();
  }, []);

  useEffect(() => {
    fetchSailInvoices();
  }, [year, offset, limit, customer, search, paid]);

  const onSendInvoice = async (invoice) => {
    showLoading();

    const isCondoInvoice = Boolean(invoice.condoId);
    const fetcher = isCondoInvoice ? createCondo : create;
    const { error, data } = await fetcher({
      newToken,
      data: invoice,
    });
    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      hideLoading();
      return;
    }

    hideLoading();
    fetchSailInvoices();
  };

  function onFilter(queryDiff) {
    setQuery(queryDiff);
  }

  const onCollectInvoice = async ({ invoice }) => {
    showLoading();

    const sailInvoice = {
      sailInvoice: {
        collected: invoice.sailInvoice?.collected || false,
        collectedDate: invoice.sailInvoice?.collectedDate || null,
        gross: Number(Number(invoice.sailInvoice?.gross).toFixed(2)) || 0,
        type: invoice.sailInvoice?.type || '',
        notPaid: Number(invoice.sailInvoice?.notPaid.toFixed(2)) || 0,
      },
    };

    const { error, data, headers } = await saveCached({
      data: sailInvoice,
      newToken,
      _id: invoice._id,
    });
    if (error) {
      setData([]);
      alert.error(t('COMMON:GENERIC_ERROR'));
      hideLoading();
      return;
    }

    alert.success(t('COMMON:SUCCESS_UPDATE'));
    fetchSailInvoices();
  };

  const searchCondos = async (value) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/condos`,
      {
        headers: { Authorization: newToken },
        params: { search: value },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data: { condos = [] } = {} } = response;
    return condos;
  };

  const searchSdiProfile = async (value) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/profiles/search/sdi`,
      {
        headers: { Authorization: newToken },
        params: { condoId: value },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = {} } = response;
    return data;
  };

  return (
    <SailInvoicesPresentational
      subjects={subjects}
      data={data}
      total={total}
      query={query}
      onFilter={onFilter}
      onSendInvoice={onSendInvoice}
      onCollectInvoice={onCollectInvoice}
      onStatusChanged={() => fetchSailInvoices()}
      searchCondos={searchCondos}
      searchSdiProfile={searchSdiProfile}
      {...props}
    />
  );
}

export default SailInvoices;
